import React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Organisation from "@/model/interface/Organisation";
import { AiAgent } from "@/model/interface/AiAgent";
import useAssignAgentForm from "@/hooks/useAssignAgentForm";
import AssignAgentForm from "./AssignAgentForm";

export default function AssignAgentDialog({
  open,
  onOpenChange,
  fixedOrganisation,
  initialTemplateId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When launched from an organisation row, the target org is fixed and not selectable. */
  fixedOrganisation?: Organisation | null;
  initialTemplateId?: string;
  onCreated?: (agent: AiAgent) => void;
}) {
  const controller = useAssignAgentForm({ fixedOrganisation, initialTemplateId });

  const handleCreate = async () => {
    const createdAgent = await controller.createAgent();
    if (!createdAgent) {
      return;
    }
    controller.reset();
    onOpenChange(false);
    onCreated?.(createdAgent);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Assign agent to organisation</DialogTitle>
          <DialogDescription>
            Grants a complimentary agent, free for the organisation and excluded from billing. Start from a
            template or copy an existing agent's configuration, then adjust its purpose below.
          </DialogDescription>
        </DialogHeader>

        <AssignAgentForm controller={controller} />

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleCreate} disabled={!controller.canSubmit}>
            {controller.isCreating ? "Assigning..." : "Assign free agent"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
